import type {VariantProps} from "@gluestack-ui/nativewind-utils";
import {tva} from "@gluestack-ui/nativewind-utils/tva";
import type {ComponentType, ReactNode} from "react";
import type React from "react";
import {memo, useCallback, useContext, useEffect, useRef} from "react";

import type {IIConProps} from "../ui/icon";
import type {PressableRef} from "../ui/pressable";
import {Pressable} from "../ui/pressable";
import {Text} from "../ui/text";

import type {TabGroupContextType} from "./context";
import {TabGroupContext} from "./context";

const styles = tva({
    base: `
        grow
        shrink
        flex-row
        items-center
        justify-center
        border
        hover:bg-blueGray-300
        hover:border-blueGray-200
        focus:border-blueGray-300
        border-transparent
        bg-transparent
    `,
    variants: {
        active: {
            true: "bg-white border-blueGray-200"
        },
        size: {
            sm: "px-1 py-0.5 gap-1 rounded-xs",
            md: "px-2 py-1 gap-1.5 rounded-sm"
        },
        useIcon: {
            true: "",
            false: "min-w-[100px]"
        }
    }
});

export type Props<T = string> = {
    id: T;
    testID?: string;
    leftIcon?: React.ReactNode;
    disabled?: boolean;
    onPress?: () => void;
} & VariantProps<typeof styles> &
    StrictUnion<
        | {
              children: ReactNode;
          }
        | {
              Icon: ComponentType<IIConProps>;
          }
    >;

function TabItem<T = string>({
    id,
    testID,
    leftIcon,
    children,
    className,
    disabled,
    Icon,
    onPress
}: Props<T>) {
    const {size, tab, onTabChange, registerTabRef} =
        useContext<TabGroupContextType<T>>(TabGroupContext);
    const tabRef = useRef<PressableRef>(null);

    useEffect(() => {
        if (tabRef.current) {
            registerTabRef(id, tabRef.current);
        }
    }, [id, registerTabRef]);

    const handlePress = useCallback(() => {
        onPress?.();
        onTabChange?.(id);
    }, [id, onTabChange, onPress]);

    const active = tab ? tab === id : false;

    return (
        <Pressable
            ref={tabRef}
            className={styles({active, className, size, useIcon: !!Icon})}
            disabled={disabled}
            role="button"
            testID={testID}
            onPress={handlePress}
        >
            {leftIcon}
            {Icon ? (
                <Icon
                    fill={active ? "blueGray.800" : "blueGray.400"}
                    size={size === "md" ? "lg" : "md"}
                />
            ) : typeof children === "string" ? (
                <Text
                    className="whitespace-nowrap"
                    color="blueGray.800"
                    variant={size === "md" ? "sm" : "xs"}
                >
                    {children}
                </Text>
            ) : (
                children
            )}
        </Pressable>
    );
}

export default memo(TabItem);
